import type { GridRenderCellParams } from "@mui/x-data-grid-pro";
import {
  type GridFilterModel,
  gridFilterModelSelector,
  type GridPaginationModel,
  gridPaginationModelSelector,
  type GridRowId,
  type GridSortModel,
  gridSortModelSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid-premium";
import { MultipleProductGroupingCell } from "src/components/DataGrid/product/MultipleProductGroupingCell.tsx";
import { ComplexProductGroupingCell } from "src/components/DataGrid/product/ComplexProductGroupingCell.tsx";
import { SetProductGroupingCell } from "src/components/DataGrid/product/SetProductGroupingCell.tsx";
import { SetComponentGroupingCell } from "src/components/DataGrid/product/SetComponentGroupingCell.tsx";
import { MultipleProductVariantGroupingCell } from "src/components/DataGrid/product/MultipleProductVariantGroupingCell.tsx";
import { SingleProductGroupingCell } from "src/components/DataGrid/product/SingleProductGroupingCell.tsx";
import { ComplexProductVariantGroupingCell } from "src/components/DataGrid/product/ComplexProductVariantGroupingCell.tsx";
import { ComplexProductSetGroupingCell } from "src/components/DataGrid/product/ComplexProductSetGroupingCell.tsx";
import { ComplexProductSetVariantGroupingCell } from "src/components/DataGrid/product/ComplexProductSetVariantGroupingCell.tsx";
import { type MouseEvent, useRef, useState } from "react";
import { logger } from "src/logger";
import {
  type ProductRowResource,
  ProductRowType,
} from "src/system/list/types.ts";

type Row = ProductRowResource;

export const ProductFakeServerTreeDataGroupingColumn = (
  props: GridRenderCellParams<Row, any, any> & {
    onFetchRows: (params: {
      row: Row;
      groupKeys: string[];
      filterModel: GridFilterModel;
      includes?: string[];
      sortModel: GridSortModel;
      paginationModel: GridPaginationModel;
    }) => Promise<{
      rows: Row[];
    }>;
    mapResourceToRow: (resource: any, params: { option?: string }) => any;
    withoutDescendants?: boolean;
    includes?: string[];
    loadingMap?: Record<GridRowId, boolean>;
    errorMap?: Record<GridRowId, Error>;
  },
) => {
  const {
    row,
    rowNode,
    field,
    id,
    withoutDescendants = false,
    onFetchRows,
    loadingMap,
    errorMap,
    includes,
  } = props;
  const apiRef = useGridApiContext();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isDataLoading = isLoading || loadingMap?.[row.id];
  const [localError, setError] = useState<Error | null>(null);
  const error = localError || errorMap?.[row.id];

  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const sortModel = useGridSelector(apiRef, gridSortModelSelector);
  const paginationModel = useGridSelector(apiRef, gridPaginationModelSelector);
  const isFetching = useRef(false);

  const getChildren = (id: string | number) => {
    const rootNode = apiRef.current.getCellParams(id, "").rowNode;
    return (rootNode.type === "group" && rootNode.children) || [];
  };

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (withoutDescendants) {
      return;
    }
    // if a row has descendants, and it is not a group yet, it means we didn't load sub rows yet
    if (row.descendants_count > 0 && rowNode.type !== "group") {
      try {
        // if there is ongoing fetch then do nothing
        // helps to avoid double fetch on accidental double click
        if (isFetching.current) {
          return;
        }
        setIsLoading(true);
        setError(null);
        isFetching.current = true;
        const response = await onFetchRows({
          groupKeys: [row.id],
          filterModel,
          includes: includes,
          paginationModel,
          sortModel,
          row,
        });
        isFetching.current = false;
        setIsLoading(false);

        // put sub-rows in the datagrid rows
        apiRef.current.setRows([
          ...apiRef.current.getSortedRows(),
          ...response.rows,
        ]);

        apiRef.current.setRowChildrenExpansion(id, true);
      } catch (error: any) {
        setError(error);
        setIsLoading(false);
        logger.error(error);
      }
    }
    if (rowNode.type === "group") {
      apiRef.current.setRowChildrenExpansion(id, !rowNode.childrenExpanded);
    }
    apiRef.current.setCellFocus(id, field);
  };

  const handleDoubleClick = (ev: MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault();
    ev.stopPropagation();
    // prevents user from redirecting to edit form on double click
  };

  const isExpanded = rowNode.type === "group" && !!rowNode.childrenExpanded;

  const isSet = row.type === ProductRowType.Set;
  const isComplex = row.type === ProductRowType.Complex;
  const isMultiple = row.type === ProductRowType.Multiple;
  const isSetComponent = row.type === ProductRowType.SetComponent;
  const isMultipleProductVariant = row.type === ProductRowType.MultipleVariant;
  const isComplexProductVariant = row.type === ProductRowType.ComplexVariant;
  const isComplexProductSet = row.type === ProductRowType.ComplexSetVariant;
  const isComplexProductSetVariant =
    row.type === ProductRowType.ComplexSetVariantComponent;

  if (isMultiple) {
    const hasChildren = row.descendants_count > 0 && !withoutDescendants;
    return (
      <MultipleProductGroupingCell
        error={error}
        isLoading={isDataLoading}
        isExpanded={isExpanded}
        hasChildren={hasChildren}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
      />
    );
  }
  if (isComplex) {
    const hasChildren = row.descendants_count > 0 && !withoutDescendants;
    return (
      <ComplexProductGroupingCell
        error={error}
        isLoading={isDataLoading}
        isExpanded={isExpanded}
        hasChildren={hasChildren}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
      />
    );
  }

  if (isSet) {
    const hasChildren = row.descendants_count > 0 && !withoutDescendants;
    return (
      <SetProductGroupingCell
        error={error}
        isLoading={isDataLoading}
        hasChildren={hasChildren}
        isExpanded={isExpanded}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
      />
    );
  }

  if (isSetComponent) {
    const rootChildren = getChildren(rowNode.parent!);
    const isLastChild = rootChildren.at(-1) === row.id;
    return <SetComponentGroupingCell isLastChild={isLastChild} />;
  }

  if (isMultipleProductVariant) {
    const rootChildren = getChildren(rowNode.parent!);
    const isLastChild = rootChildren.at(-1) === row.id;
    return <MultipleProductVariantGroupingCell isLastChild={isLastChild} />;
  }

  if (isComplexProductVariant) {
    const rootChildren = getChildren(rowNode.parent!);
    const isLastChild = rootChildren.at(-1) === row.id;
    return <ComplexProductVariantGroupingCell isLastChild={isLastChild} />;
  }

  if (isComplexProductSet) {
    const rootChildren = getChildren(rowNode.parent!);
    const isLastChild = rootChildren.at(-1) === row.id;

    const hasChildren = row.descendants_count > 0;
    return (
      <ComplexProductSetGroupingCell
        error={error}
        isExpanded={isExpanded}
        isLoading={isDataLoading}
        hasChildren={hasChildren}
        isLastChild={isLastChild}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
      />
    );
  }

  if (isComplexProductSetVariant) {
    const rootChildren = getChildren(rowNode.parent!);
    const parentRowNode = apiRef.current.getCellParams(
      rowNode.parent!,
      "",
    ).rowNode;
    const parentChildren = getChildren(parentRowNode.parent!);
    const isParentLastChild = parentChildren.at(-1) === parentRowNode.id;
    const isLastChild = rootChildren.at(-1) === row.id;
    return (
      <ComplexProductSetVariantGroupingCell
        isLastChild={isLastChild}
        isParentLastChild={isParentLastChild}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
      />
    );
  }

  return <SingleProductGroupingCell />;
};
